// New-system user guide (the video guide on the home page)
const userGuide = {
  card: {
    eyebrow: 'New system · Video guide',
    duration: '{{minutes}} min',
    title: 'New Portal User Guide',
    text: 'Everything on the home page and your profile, step by step. Pick a topic and watch just that part.',
    watch: 'Start Watching',
    resume: 'Continue Where I Left Off',
    restart: 'Watch from the start',
    watched: '{{done}}/{{total}} topics watched',
    allWatched: 'You have watched every topic',
    topics: 'Popular topics',
    oldGuide: 'Old system guide (PDF)',
  },
  player: {
    title: 'New Portal User Guide',
    subtitle: 'Home page and profile · {{count}} topics · {{minutes}} min',
    searchPlaceholder: 'Search: leave, expense, password...',
    searchLabel: 'Search the guide',
    noResults: 'No topic matches "{{term}}". Try another word.',
    matchedSteps_one: '{{count}} matching step',
    matchedSteps_other: '{{count}} matching steps',
    topics: 'Topics',
    steps: 'Steps in this chapter',
    stepsHint: 'Click a step to play the video from there.',
    watched: 'Watched',
    nowPlaying: 'Now playing',
    chapterOf: 'Chapter {{current}} of {{total}}',
    demoNote: 'The screens are an animation with sample data; none of your real information is shown.',
    keyboard: 'Space: play / pause · ← →: previous / next step · M: mute / unmute · F: full screen',
    oldGuide: 'Old system guide (PDF)',
    close: 'Close the guide',
    showTopics: 'Show topics',
    hideTopics: 'Hide topics',
    ended: 'End of the guide',
    endedText: 'To watch a topic again, pick it from the list.',
    replay: 'Watch from the start',
    controls: {
      seek: 'Video position',
      play: 'Play',
      pause: 'Pause',
      replay: 'Play again',
      prevChapter: 'Previous chapter',
      nextChapter: 'Next chapter',
      speed: 'Playback speed',
      captionsOn: 'Turn captions off',
      captionsOff: 'Turn captions on',
      mute: 'Mute',
      unmute: 'Unmute',
      volume: 'Volume',
      voiceUnavailable: 'Your browser does not support narration',
      fullscreen: 'Full screen',
      exitFullscreen: 'Exit full screen',
    },
  },
  groups: {
    start: 'Getting started',
    home: 'Home page',
    profile: 'Profile page',
    end: 'Wrap-up',
  },
  chapters: {
    intro: 'How to use this guide',
    shell: 'Menu, search and top bar',
    dashboard: 'Home page',
    tasks: 'My tasks',
    projectsDialog: 'Projects window',
    reminders: 'Reminders and notifications',
    profileCard: 'Profile card and my details',
    completion: 'Profile completion',
    tabs: 'Profile sections',
    calendar: 'My calendar',
    agenda: 'Agenda',
    files: 'Files',
    sop: 'Reading SOPs',
    training: 'Training',
    myProjects: 'My projects',
    leaveRequest: 'Requesting leave',
    leaveList: 'My leave requests',
    expenseRequest: 'Claiming an expense',
    expenseTrack: 'Tracking expenses',
    messages: 'Messages',
    announcements: 'Announcements',
    bugReport: 'Reporting a bug',
    cv: 'CV / GCP / IATA',
    end: 'Wrap-up',
  },
  // Extra words the search matches (chapter titles and captions are searched too)
  keywords: {
    intro: 'guide help video how to use tutorial',
    shell: 'menu sidebar search ctrl k hall sign out log out session theme dark light language turkish bell timeout 10 minutes',
    dashboard: 'home dashboard quick access welcome',
    tasks: 'task to-do list status completed overdue priority bell',
    projectsDialog: 'project projects protocol site centre report excel details patient visit',
    reminders: 'reminder alert notification deadline missing document message notification',
    profileCard: 'profile photo picture phone emergency address home work password change contact details',
    completion: 'profile completion percent missing ring',
    tabs: 'tab section menu shipments cargo',
    calendar: 'calendar visit job add excel month',
    agenda: 'agenda google calendar event meeting',
    files: 'file document form policy job description download print',
    sop: 'sop read understood confirm quiz exam assessment',
    training: 'training video certificate exam pop quiz final notes bookmark',
    myProjects: 'my projects visit inactive passive primary backup blinding excel',
    leaveRequest: 'leave annual excuse sick report holiday balance days travel request vacation',
    leaveList: 'leave requests approved rejected pending status',
    expenseRequest: 'expense claim receipt invoice pdf amount category request',
    expenseTrack: 'expense tracking edit rejected approved reason',
    messages: 'message chat file send emoji voice video call',
    announcements: 'announcement notification news',
    bugReport: 'bug report problem error screenshot support',
    cv: 'cv resume gcp iata certificate upload template transcelerate ministry',
    end: 'end finish',
  },
  beats: {
    intro: 'This guide shows how to do your daily work in the redesigned HabCare portal: first the general layout and the home page, then every section of your profile page.',
    howTo: 'You don’t have to watch it all. Pick a topic from the list, type a word such as "leave" or "expense" into the search, or click one of the steps below to jump straight to that moment.',
    shellSidebar: 'The menu on the left takes you to the home page and your profile. The arrow next to Home opens its submenu, where you can also reach the To-do list.',
    shellCollapse: 'The small button on the edge of the menu collapses and expands it. At the bottom, Hall takes you back to the system picker and Sign out ends your session. On a phone, open the menu with the menu button in the top bar.',
    shellSearch: 'The search box in the top bar searches the menu; Ctrl + K opens it too. Typing "Prof" is enough to find Profile, and clicking the result takes you there.',
    shellIcons: 'The icons on the right are Hall, Home, the bell that counts your new tasks, Turkish / English and light / dark theme. When you have unread messages or missed calls, another icon appears here.',
    shellUser: 'The button with your name opens the account menu, where you can go to your profile or sign out.',
    shellTimeout: 'For your security, you are signed out automatically after 10 minutes without any activity; just sign in again. Your session is kept open while this guide is playing.',
    dashHero: 'At the top of the home page, a card greets you according to the time of day. It is the first thing you see when you sign in.',
    dashTasks: 'The To-do list card shows the tasks assigned to you. When a new task arrives, the card is highlighted in red and shows how many new tasks you have.',
    dashGuide: 'This guide lives on the home page too. The big play button starts the video, and the topic buttons next to it take you straight to a section such as leave, expenses or files.',
    dashLinks: 'Among the quick access cards, My calendar and Announcements open those sections of your profile, and My projects opens the projects window. The User guide card still holds the old system’s PDF guide.',
    tasksOpen: 'To see your tasks, press the button on the To-do list card or the bell in the top bar; the My tasks window opens.',
    tasksStats: 'The boxes at the top count your pending, in-progress, completed and overdue tasks. Use the buttons below them to show Active, Completed or All tasks.',
    tasksCard: 'Each card shows the task’s title, priority and due date. The colour on the left is the priority; late tasks are marked Overdue and new ones New.',
    tasksStatus: 'Tap a task to see its description. Under Change status, mark it In progress or Completed; the change is saved right away.',
    projOpen: 'The My projects card opens the Projects window with the projects you work on.',
    projFilter: 'Type a project code, protocol, customer or site into the search box to narrow the list. You can filter by assignment and project status, and switch between assigned and all projects with the button at the top.',
    projDetail: 'The Details button opens the project: protocol, customer and dates, the sites and roles you are assigned to, and a summary of your own patients and visits.',
    projReport: 'Export report downloads the projects in the list as an Excel file.',
    remLogin: 'If something is waiting for you when you sign in, a reminder opens: your unread SOPs and missing documents such as your CV, GCP or IATA are listed here.',
    remButtons: 'Go to SOPs and Upload my documents take you straight to the right section. If you press Later, the window closes and you are reminded again at your next sign-in until the items are done.',
    remBanner: 'If an SOP or training is close to its deadline, a red alert appears at the top of the page. The button on it takes you to the Files section.',
    remMessage: 'When a new message arrives, a notification card appears at the bottom right; click it to open the message.',
    pcGo: 'Reach your profile page from the menu on the left or from the account menu at the top right. The rest of this guide covers that page.',
    pcOpen: 'The profile card shows your photo, name, role and today’s date. At the top right are the profile completion ring and the Change Password button.',
    pcPhoto: 'To change your photo, press the camera icon in the corner and choose a picture. Check how it looks on your profile, on cards and in the menu, then press Save.',
    pcStats: 'The three boxes below are a quick summary: your active projects, the SOPs you have read and your unread messages.',
    pcContacts: 'You can update your emergency phone number and addresses yourself. Press Add or Edit, type the number and press Save.',
    pcAddress: 'In the address window, the home address is required and the work address is optional. Use the star buttons to choose which one is your main address, then save.',
    pcPassword: 'Press Change Password, then type your current password and your new password twice. The new password needs at least 6 characters and both fields must match.',
    compRing: 'The profile completion ring shows how complete your profile is. Press it to see separate progress for personal details, photo and training.',
    compActions: 'Each line under the to-do list takes you to what is missing: a photo, an emergency phone, your home address or pending SOPs and training. For other details such as name, role or e-mail, contact your manager.',
    tabsGroups: 'When you scroll down, the section menu stays at the top. Calendar, Files, My Projects, Leave, Expenses, Messages and CV / GCP / IATA are here.',
    tabsSub: 'Some sections have sub-tabs. For example, pressing Leave shows Request Leave and My Leave Requests underneath.',
    tabsBadges: 'The numbers next to section names show what is waiting for you: unread documents in Files, unread messages in Messages. If you have access, a My Shipments section appears too.',
    calOverview: 'My Calendar shows your visits and jobs in a month view. Press a day and its plan is listed in the panel on the right.',
    calAdd: 'Add Visit and Add Job add entries to your calendar; the Excel button downloads your calendar as a file.',
    calVideo: 'For the calendar in detail, see How to read it? and the Video guide button; adding and completing visits is explained there step by step.',
    agOpen: 'The Agenda tab lets you quickly add an event to your Google Calendar.',
    agFill: 'Enter the event title, a description if you like, and the start and end date and time.',
    agSend: 'When you press Add to Google Calendar, Google Calendar opens in a new tab with the details filled in; press Save there to add the event. This tab itself keeps no records.',
    filesCards: 'The Files section holds the SOPs, training, forms, policies and job descriptions assigned to you, as cards. The numbers on the cards show new and unread documents.',
    filesOpen: 'Press a card and that category’s list opens below. New documents appear at the top with a yellow background and a New label.',
    filesFilter: 'Filter the SOP list by Unread or Read, and use the search box to find a document by name.',
    filesForms: 'Press a form and its file opens in a new tab, where you can download or print it. Policies and job descriptions open the same way.',
    sopOpen: 'To read an SOP, press it in the list; the document opens in the viewer.',
    sopLock: 'The confirmation box below is locked at first; it unlocks a few seconds after the document loads, or when a video has been watched. Please read the document to the end.',
    sopConfirm: 'When you tick "I have read and understood this SOP", a short assessment specific to your department opens.',
    sopQuiz: 'Answer the questions one by one and move on with Next. If you score below the pass mark, review the document and try again with new questions.',
    sopDone: 'When you pass, the SOP is marked Read and leaves the unread list.',
    trList: 'The Training card lists your video courses. Those with a deadline show the date, finished ones are marked Completed, and expired ones show Expired.',
    trPlayer: 'A course opens in its own player and continues where you left off. You cannot skip parts you have not watched yet; on the right you see the chapters, your notes and bookmarks.',
    trPop: 'Pop Quiz questions appear while you watch. A correct answer lets you continue; after a wrong answer you are asked to watch that chapter again.',
    trExam: 'When you have watched almost the whole video, the Final exam unlocks. Pass it to view your certificate and complete the course.',
    mpCards: 'The My Projects section shows the projects you work on as cards: code, name, whether you are primary or backup, and your blinding status. Hide inactive projects with the button at the top right.',
    mpVisits: 'Tap a project to open your visits in it. Pick a start and end date to narrow the list.',
    mpDetail: 'Open a completed visit with Details. Download Excel saves the visits on screen as a file.',
    lrType: 'To request leave, go to the Leave section. On Request Leave, first choose the leave type; each type has a short explanation.',
    lrBalance: 'For annual leave, your balance is shown: total entitlement, days used, days awaiting approval and days remaining.',
    lrDates: 'Choose the start and end dates. The number of days is calculated for you: Saturday counts as half a day, Sundays and public holidays are not counted.',
    lrMore: 'Enter travel days if any; half days are fine. Add the reason and the address where you will stay. For sick leave you can also attach your medical report.',
    lrSend: 'Press Send Leave Request. It goes to your manager for approval and, once approved, is added to your calendar.',
    llList: 'My Leave Requests lists every request you have sent with its dates, length and status.',
    llFilter: 'Use the buttons at the top to see Pending, Approved or Rejected requests separately.',
    llNote: 'If your manager added a note, it appears under the request; for rejected requests, this is where you read the reason.',
    erForm: 'In the Expenses section, go to Claim Expense. Enter the amount, the category and what the money was spent on, where and when.',
    erDocs: 'Attach your receipts and invoices as PDFs. You can choose several files; each must be 10 MB or less.',
    erSend: 'Press Send Expense Claim. It goes to the finance managers, and you follow its status on My Expenses.',
    etList: 'My Expenses shows your total, pending, approved and rejected claims. Each card shows the amount, category, description and documents.',
    etEdit: 'You can change a claim that is not approved yet with Edit: update the amount and description, or replace the documents.',
    etRejected: 'For a rejected claim, the reason is written on the card. Fix what was missing and create a new claim.',
    msgOpen: 'Messages are in the Messages section. So that the page opens quickly, your messages load when you press Open Messages.',
    msgPick: 'Search the list on the left to find a colleague and press their name. Unread messages are shown with a number.',
    msgSend: 'Type your message in the box at the bottom and press send. Use the emojis above the message box to add one.',
    msgFile: 'Use the paper clip to choose and send a file or photo; it appears in the chat and the other person can download it.',
    msgCall: 'Start a voice or video call with the buttons at the top right. Calls you miss appear as Missed calls in the top bar.',
    annList: 'The Announcements tab holds company-wide announcements, newest first. The Announcements card on the home page brings you straight here too.',
    bugForm: 'If something goes wrong in the portal, use the Report a Bug tab in the Messages section. Write what you were trying to do, on which page, and what happened.',
    bugShot: 'Adding a screenshot helps us fix the problem much faster. Press Send Report and it goes to the software team.',
    cvTabs: 'The CV / GCP / IATA section has separate tabs for your Turkish CV, English CV, GCP and IATA documents.',
    cvTemplate: 'Download the Ministry CV form for your Turkish CV or the TransCelerate template for your English CV here, then fill it in.',
    cvUpload: 'Choose your file or drag it into the box: PDF, DOC or DOCX, up to 10 MB. Press Upload and the file appears in the list on the left.',
    cvReplace: 'You can view, download or delete an uploaded file. CV and GCP keep a single file, so delete the old one first to replace it. IATA accepts several files.',
    end: 'That’s it! Whenever you want to see a topic again, open this guide from the home page and go straight to it from the topic list or the search.',
  },
  // Sample content in the animation
  stage: {
    introText: 'Learn your daily work in the new portal, step by step.',
    howtoTitle: 'Find what you need right away',
    howtoTopics: 'Pick a topic',
    howtoSearch: 'Search',
    howtoSteps: 'Jump to a step',
    howtoQuery: 'leave',
    howtoStep1: 'Choose the leave type',
    howtoStep2: 'Enter the dates',
    howtoStep3: 'Send the request',
    endTitle: 'You’re all set!',
    endText: 'The guide is always on the home page. Come back to any topic whenever you need it.',
    deadlineTitle: '2 assignments are close to their deadline',
    deadlineItems: 'SOP-014 Informed Consent · 2 days left  ·  Good Clinical Practice training · 6 days left',
    complete: 'Complete',
    messagePreview: 'I prepared the sample kits for tomorrow’s visit 👍',
    messagePreview2: 'I shared the meeting notes.',
    messagePreview3: 'Thanks, see you!',
    myMessage: 'Great, thank you! I’ll be at the site at 9 am.',
    task1: 'Update the site file',
    task1Desc: 'Add the source documents of the June visits to the site file and report anything missing to the project manager.',
    task2: 'Renew the GCP certificate',
    task3: 'Send the monthly visit report',
    p1: 'Oncology Phase III Study',
    p2: 'Cardiology Observational Study',
    p3: 'Neurology Phase II Study',
    center: 'Ankara University Hospital',
    sop1: 'Informed Consent Process',
    sop2: 'Sample Shipment',
    sop3: 'Adverse Event Reporting',
    training1: 'Good Clinical Practice (GCP)',
    training2: 'Data Privacy and KVKK',
    training3: 'Preparing Sample Kits',
    training4: 'Annual OHS Training',
    form1: 'Visit Checklist Form',
    form2: 'Sample Shipment Form',
    quizQuestion: 'When must the informed consent form be signed?',
    quizChoice1: 'After the first visit',
    quizChoice2: 'Before any study-specific procedure',
    quizChoice3: 'After randomisation',
    quizChoice4: 'When the study ends',
    popQuestion: 'Under GCP, who must be able to inspect study records?',
    popChoice1: 'Only the sponsor',
    popChoice2: 'Monitors, auditors and the competent authorities',
    popChoice3: 'Nobody',
    dayTitle: 'Today’s plan',
    plannedToday: 'Planned',
    agendaTitle: 'Project meeting',
    agendaDesc: 'Monthly status review',
    leaveReason: 'Family visit',
    homeAddress: 'Kızılay Mah. Atatürk Blv. No:12, Çankaya / Ankara',
    noteApproved: 'Enjoy your holiday!',
    noteRejected: 'We have a monitoring visit at the site on those dates; let’s plan another day.',
    expenseDesc: 'Taxi to the site visit',
    expenseDesc2: 'Hotel for the İzmir visit',
    expenseDesc3: 'Meeting lunch',
    expenseReject: 'The receipt is unreadable, please attach a clear copy.',
    announcement1: 'Our office will be closed on 29 October for Republic Day.',
    announcement2: 'The new portal is live! Watch the guide on the home page to see how it works.',
    announcement3: 'Annual GCP training starts in November.',
    bugText: 'When I attach a PDF to an expense claim and press "Send", the page stops responding.',
  },
};

export default userGuide;
